import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../App";
import { authAxios } from "../utils/auth";
import { 
  ArrowLeft, Coins, ChevronDown, ChevronUp, 
  Filter, Calendar, User, RefreshCw, X, TrendingUp, TrendingDown, ArrowRightLeft,
  Zap, DollarSign, Copy, Check, ExternalLink
} from "lucide-react";
import { formatInTimezone } from "../utils/timezone";
import AdminMenu from "../components/AdminMenu";

const PromptLogCredits = () => {
  const navigate = useNavigate();
  const { userId: urlUserId } = useParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [totals, setTotals] = useState({
    total_credits: 0,
    total_debits: 0,
    net_change: 0,
    transaction_count: 0
  });
  const [pagination, setPagination] = useState({
    skip: 0,
    limit: 50,
    has_more: false,
    total_count: 0
  });
  const [availableUsers, setAvailableUsers] = useState([]);
  const [availableTypes, setAvailableTypes] = useState([]);
  const [showTotalsDropdown, setShowTotalsDropdown] = useState(false);
  
  // Modal state for viewing LLM_USAGE logs
  const [selectedLog, setSelectedLog] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [fullPromptData, setFullPromptData] = useState(null);
  const [copied, setCopied] = useState(false);
  
  // Filter states
  const [selectedType, setSelectedType] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [userId, setUserId] = useState(urlUserId || "");
  
  // Applied filters
  const [appliedFilters, setAppliedFilters] = useState({
    type: "",
    fromDate: "",
    toDate: "",
    userId: urlUserId || ""
  });

  // Get user timezone
  const userTimezone = user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  const fetchLedger = useCallback(async (skip = 0, filters = appliedFilters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("skip", skip);
      params.append("limit", 50);
      
      if (filters.type) params.append("transaction_type", filters.type);
      if (filters.fromDate) params.append("from_date", new Date(filters.fromDate).toISOString());
      if (filters.toDate) params.append("to_date", new Date(filters.toDate).toISOString());
      if (filters.userId) params.append("user_id", filters.userId);
      
      const response = await authAxios.get(`/credits/admin/ledger?${params.toString()}`);
      
      setTransactions(response.data.transactions || []);
      setTotals(response.data.totals || {});
      setPagination(response.data.pagination || {});
      setAvailableUsers(response.data.available_users || []);
      setAvailableTypes(response.data.available_types || []);
    } catch (error) {
      console.error("Failed to fetch ledger:", error);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => {
    // If URL has userId, fetch with that filter applied
    if (urlUserId) {
      const initialFilters = { ...appliedFilters, userId: urlUserId };
      setAppliedFilters(initialFilters);
      fetchLedger(0, initialFilters);
    } else {
      fetchLedger(0);
    }
  }, [urlUserId]);

  const applyFilters = () => {
    const newFilters = {
      type: selectedType,
      fromDate,
      toDate,
      userId
    };
    setAppliedFilters(newFilters);
    fetchLedger(0, newFilters);
  };

  const clearFilters = () => {
    setSelectedType("");
    setFromDate("");
    setToDate("");
    setUserId("");
    const emptyFilters = { type: "", fromDate: "", toDate: "", userId: "" };
    setAppliedFilters(emptyFilters);
    fetchLedger(0, emptyFilters);
  };

  const hasActiveFilters = appliedFilters.type || appliedFilters.fromDate || appliedFilters.toDate || appliedFilters.userId;

  const currentPage = Math.floor(pagination.skip / pagination.limit) + 1;
  const totalPages = Math.ceil(pagination.total_count / pagination.limit);

  // Format transaction type for display
  const formatType = (type) => {
    const typeColors = {
      TEST_CR: "bg-blue-500/20 text-blue-400",
      TEST_DB: "bg-orange-500/20 text-orange-400",
      PURCHASE: "bg-green-500/20 text-green-400",
      TASK_REWARD: "bg-purple-500/20 text-purple-400",
      SUBSCRIPTION: "bg-cyan-500/20 text-cyan-400",
      BONUS: "bg-pink-500/20 text-pink-400",
      LLM_USAGE: "bg-red-500/20 text-red-400",
      INCENTIVE: "bg-yellow-500/20 text-yellow-400",
      MYCHAT: "bg-indigo-500/20 text-indigo-400"
    };
    return typeColors[type] || "bg-gray-500/20 text-gray-400";
  };

  // Handle row click for LLM_USAGE entries
  const handleRowClick = async (tx) => {
    // Only handle LLM_USAGE type
    if (tx.type !== "LLM_USAGE") return;
    
    // Get log_id from reference_id and session_id from metadata
    const logId = tx.reference_id;  // reference_id is the log_id
    const sessionId = tx.metadata?.session_id;
    
    if (!logId || !sessionId) {
      console.warn("Missing log_id or session_id for LLM_USAGE entry", { logId, sessionId, tx });
      return;
    }
    
    setSelectedLog({ ...tx, log_id: logId, session_id: sessionId });
    setModalLoading(true);
    setFullPromptData(null);
    
    try {
      // Fetch full prompt data from session
      const response = await authAxios.get(`/admin/plog/session/${sessionId}?limit=1000&skip=0`);
      const prompts = response.data.prompts || [];
      // Find the specific prompt by log_id
      const fullPrompt = prompts.find(p => p.log_id === logId);
      setFullPromptData(fullPrompt || { ...tx, log_id: logId, session_id: sessionId });
    } catch (error) {
      console.error("Failed to fetch prompt details:", error);
      setFullPromptData({ ...tx, log_id: logId, session_id: sessionId });
    } finally {
      setModalLoading(false);
    }
  };

  const closeModal = () => {
    setSelectedLog(null);
    setFullPromptData(null);
    setCopied(false);
  };

  const copyContent = () => {
    const content = fullPromptData?.content || "";
    navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const openFullPage = () => {
    if (selectedLog) {
      navigate(`/plog/prompt/${selectedLog.session_id}/${selectedLog.log_id}`);
    }
  };

  const formatCost = (cost) => {
    if (cost === null || cost === undefined) return "—";
    if (cost === 0) return "$0.00";
    if (cost < 0.0001) return `$${cost.toExponential(2)}`;
    if (cost < 0.01) return `$${cost.toFixed(6)}`;
    return `$${cost.toFixed(4)}`;
  };

  return (
    <div className="min-h-screen bg-divine-black text-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-divine-black/95 backdrop-blur border-b border-divine-gold/20">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Left: Back link and title */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/plog")}
                className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Back to Prompt Log</span>
              </button>
              <h1 className="font-cinzel text-xl text-divine-gold">Coin Ledger</h1>
            </div>
            
            {/* Right: Totals display */}
            <div className="flex items-center gap-2">
            <div className="relative">
              <button
                onClick={() => setShowTotalsDropdown(!showTotalsDropdown)}
                className="flex items-center gap-4 px-4 py-2 bg-white/5 rounded-lg border border-divine-gold/30 hover:bg-white/10 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Coins className="w-4 h-4 text-amber-400" />
                  <span className="text-white font-medium">
                    {totals.net_change?.toLocaleString() || 0}
                  </span>
                  <span className="text-white/40 text-sm">net</span>
                </div>
                <div className="w-px h-6 bg-white/20" />
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-green-400" />
                  <span className="text-green-400 font-medium">
                    +{totals.total_credits?.toLocaleString() || 0}
                  </span>
                </div>
                <div className="w-px h-6 bg-white/20" />
                <div className="flex items-center gap-2">
                  <TrendingDown className="w-4 h-4 text-red-400" />
                  <span className="text-red-400 font-medium">
                    -{totals.total_debits?.toLocaleString() || 0}
                  </span>
                </div>
                {showTotalsDropdown ? (
                  <ChevronUp className="w-4 h-4 text-white/40" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-white/40" />
                )}
              </button>
              
              {/* Totals Dropdown */}
              {showTotalsDropdown && (
                <div className="absolute right-0 top-full mt-2 w-72 bg-divine-charcoal border border-divine-gold/30 rounded-lg shadow-xl z-50 p-4">
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-white/60">Total Transactions</span>
                      <span className="text-white font-medium">{totals.transaction_count?.toLocaleString()}</span>
                    </div>
                    <div className="border-t border-white/10 pt-3">
                      <div className="flex justify-between">
                        <span className="text-white/60">Total Credits</span>
                        <span className="text-green-400 font-medium">+{totals.total_credits?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between mt-2">
                        <span className="text-white/60">Total Debits</span>
                        <span className="text-red-400 font-medium">-{totals.total_debits?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between mt-2 pt-2 border-t border-white/10">
                        <span className="text-white/60">Net Change</span>
                        <span className={`font-medium ${totals.net_change >= 0 ? 'text-amber-400' : 'text-red-400'}`}>
                          {totals.net_change >= 0 ? '+' : ''}{totals.net_change?.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <AdminMenu />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Filters Section */}
        <div className="mb-6 p-4 bg-white/5 rounded-lg border border-white/10">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-4 h-4 text-divine-gold" />
            <span className="text-white/80 font-medium">Filters</span>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="ml-auto flex items-center gap-1 px-2 py-1 text-xs text-red-400 hover:text-red-300 transition-colors"
              >
                <X className="w-3 h-3" />
                Clear All
              </button>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Transaction Type Filter */}
            <div>
              <label className="block text-white/60 text-xs mb-1">Type</label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-full px-3 py-2 bg-divine-charcoal border border-white/20 rounded-lg text-white text-sm focus:border-divine-gold/50 focus:outline-none"
              >
                <option value="">All Types</option>
                {availableTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            
            {/* User Filter */}
            <div>
              <label className="block text-white/60 text-xs mb-1">User ID</label>
              <input
                type="text"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                placeholder="Enter user ID..."
                className="w-full px-3 py-2 bg-divine-charcoal border border-white/20 rounded-lg text-white text-sm focus:border-divine-gold/50 focus:outline-none placeholder:text-white/30"
              />
            </div>
            
            {/* From Date */}
            <div>
              <label className="block text-white/60 text-xs mb-1">From Date</label>
              <input
                type="datetime-local"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-full px-3 py-2 bg-divine-charcoal border border-white/20 rounded-lg text-white text-sm focus:border-divine-gold/50 focus:outline-none"
              />
            </div>
            
            {/* To Date */}
            <div>
              <label className="block text-white/60 text-xs mb-1">To Date</label>
              <input
                type="datetime-local"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-full px-3 py-2 bg-divine-charcoal border border-white/20 rounded-lg text-white text-sm focus:border-divine-gold/50 focus:outline-none"
              />
            </div>
            
            {/* Apply Button */}
            <div className="flex items-end">
              <button
                onClick={applyFilters}
                className="w-full px-4 py-2 bg-divine-gold/20 text-divine-gold border border-divine-gold/30 rounded-lg hover:bg-divine-gold/30 transition-colors text-sm font-medium"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white/5 rounded-lg border border-white/10 overflow-hidden">
          {/* Table Header */}
          <div className="grid grid-cols-[1fr_1.2fr_0.6fr_0.8fr_0.8fr_1.5fr_0.8fr] gap-4 px-4 py-3 bg-white/5 border-b border-white/10 text-xs text-white/60 font-medium">
            <div>Time</div>
            <div>User</div>
            <div>Type</div>
            <div className="text-right">Amount</div>
            <div className="text-right">Balance After</div>
            <div>Reference</div>
            <div>Details</div>
          </div>
          
          {/* Table Body */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 text-divine-gold animate-spin" />
            </div>
          ) : transactions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-white/40">
              <Coins className="w-12 h-12 mb-4 opacity-50" />
              <p>No transactions found</p>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {transactions.map((tx) => (
                <div 
                  key={tx.id} 
                  onClick={() => handleRowClick(tx)}
                  className={`grid grid-cols-[1fr_1.2fr_0.6fr_0.8fr_0.8fr_1.5fr_0.8fr] gap-4 px-4 py-3 hover:bg-white/5 transition-colors items-center ${
                    tx.type === "LLM_USAGE" ? "cursor-pointer" : ""
                  }`}
                >
                  {/* Time */}
                  <div className="text-sm text-white/60">
                    {tx.created_at ? formatInTimezone(new Date(tx.created_at), userTimezone) : "—"}
                  </div>
                  
                  {/* User */}
                  <div className="flex flex-col">
                    <span className="text-sm text-white truncate" title={tx.user_name || tx.user_id}>
                      {tx.user_name || tx.user_id?.slice(0, 12)}
                    </span>
                    <span className="text-xs text-white/40 truncate" title={tx.user_id}>
                      {tx.user_id}
                    </span>
                  </div>
                  
                  {/* Type */}
                  <div>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${formatType(tx.type)}`}>
                      {tx.type}
                    </span>
                  </div>
                  
                  {/* Amount */}
                  <div className={`text-right text-sm font-medium ${tx.amount >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {tx.amount >= 0 ? '+' : ''}{tx.amount?.toLocaleString()}
                  </div>
                  
                  {/* Balance After */}
                  <div className="text-right text-sm text-amber-400 font-medium">
                    {tx.balance_after?.toLocaleString()}
                  </div>
                  
                  {/* Reference */}
                  <div className="text-xs text-white/60 truncate" title={tx.reference_id || "—"}>
                    {tx.reference_id || "—"}
                  </div>
                  
                  {/* Details */}
                  <div className="text-xs text-white/40 truncate">
                    {tx.details || tx.metadata?.source || tx.metadata?.log_id?.slice(0, 12) || "—"}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination.total_count > 0 && (
          <div className="flex items-center justify-between mt-4 text-sm">
            <div className="text-white/60">
              Showing {pagination.skip + 1} - {Math.min(pagination.skip + pagination.limit, pagination.total_count)} of {pagination.total_count}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => fetchLedger(pagination.skip - pagination.limit)}
                disabled={pagination.skip === 0}
                className="px-3 py-1 bg-white/10 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/20 transition-colors"
              >
                Previous
              </button>
              <span className="px-3 py-1 text-white/60">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => fetchLedger(pagination.skip + pagination.limit)}
                disabled={!pagination.has_more}
                className="px-3 py-1 bg-white/10 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/20 transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </main>

      {/* Prompt Preview Modal for LLM_USAGE entries */}
      {selectedLog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={closeModal}>
          <div 
            className="bg-divine-charcoal border border-divine-gold/30 rounded-xl shadow-2xl w-full max-w-4xl max-h-[80vh] flex flex-col m-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <div className="flex items-center gap-4">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  fullPromptData?.prompt_type === "assistant" ? "bg-blue-500/20 text-blue-300" :
                  fullPromptData?.prompt_type === "user" ? "bg-green-500/20 text-green-300" :
                  fullPromptData?.prompt_type === "system" ? "bg-purple-500/20 text-purple-300" :
                  "bg-white/10 text-white/60"
                }`}>
                  {fullPromptData?.prompt_type || "LLM Usage"}
                </span>
                <span className="text-white/60 text-sm">{fullPromptData?.agent_name || selectedLog.metadata?.agent || "unknown agent"}</span>
                <span className="text-white/40 text-sm">•</span>
                <span className="text-white/60 text-sm">{fullPromptData?.model || selectedLog.metadata?.model || "—"}</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={copyContent}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  title="Copy content"
                >
                  {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4 text-white/60" />}
                </button>
                <button
                  onClick={openFullPage}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  title="Open full page"
                >
                  <ExternalLink className="w-4 h-4 text-white/60" />
                </button>
                <button
                  onClick={closeModal}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-white/60" />
                </button>
              </div>
            </div>
            
            {/* Modal Metadata */}
            <div className="px-6 py-3 bg-white/5 border-b border-white/10 flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-white/40" />
                <span className="text-white/60">{selectedLog.user_name || "Unknown"}</span>
                <span className="text-white/30 text-xs">({selectedLog.user_id})</span>
              </div>
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-divine-gold" />
                <span className="text-white/80">{(fullPromptData?.total_tokens || fullPromptData?.input_tokens + fullPromptData?.output_tokens || 0).toLocaleString()} tokens</span>
              </div>
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-green-400" />
                <span className="text-green-400">{formatCost(fullPromptData?.estimated_cost || fullPromptData?.actual_cost || 0)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Coins className="w-4 h-4 text-amber-400" />
                <span className="text-amber-400">{Math.abs(selectedLog.amount || 0).toLocaleString()} coins</span>
              </div>
              <div className="text-white/40">
                {selectedLog.created_at ? formatInTimezone(new Date(selectedLog.created_at), userTimezone) : ""}
              </div>
            </div>
            
            {/* Modal Content */}
            <div className="flex-1 overflow-auto p-6">
              {modalLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-6 h-6 animate-spin text-divine-gold" />
                </div>
              ) : (
                <pre className="whitespace-pre-wrap text-white/80 text-sm font-mono leading-relaxed">
                  {fullPromptData?.content || "No content available"}
                </pre>
              )}
            </div>
            
            {/* Modal Footer */}
            <div className="px-6 py-3 border-t border-white/10 flex items-center justify-between text-xs text-white/40 font-mono">
              <div className="flex items-center gap-4">
                <span>Log: {selectedLog.log_id?.slice(0, 8)}...</span>
                {(fullPromptData?.request_id) && (
                  <span>Request: {fullPromptData.request_id}</span>
                )}
              </div>
              <button 
                onClick={() => {
                  closeModal();
                  navigate(`/plog?session=${selectedLog.session_id}`);
                }}
                className="hover:text-divine-gold hover:underline transition-colors cursor-pointer"
                title="View session in Prompt Log"
              >
                Session: {selectedLog.session_id}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromptLogCredits;
